import { useEffect } from "react";
import { Link } from "react-router-dom";
import { seoArticles } from "@/mocks/seo-articles";
import { articleContent, getRelatedArticleInfo } from "@/pages/articles/data";
import JsonLd from "@/components/base/JsonLd";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface ArticleLayoutProps {
  slug: string;
}

function buildArticleSchema(article: typeof seoArticles[0], content: typeof articleContent[string]) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `https://yoybuy.help/${article.slug}#article`,
    "headline": content.heroTitle,
    "description": content.heroDescription,
    "datePublished": article.date,
    "dateModified": article.date,
    "inLanguage": "ru-RU",
    "author": {
      "@type": "Organization",
      "name": "Yoybuy Sheet",
      "url": "https://yoybuy.help/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Yoybuy Sheet",
      "url": "https://yoybuy.help/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://yoybuy.help/${article.slug}`
    },
    "image": article.image
  };
}

export default function ArticleLayout({ slug }: ArticleLayoutProps) {
  const article = seoArticles.find((a) => a.slug === slug);
  const content = articleContent[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!article || !content) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 pb-16 px-4 text-center">
          <h1 className="font-heading font-bold text-2xl text-foreground-950">Статья не найдена</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline cursor-pointer">Вернуться на главную</Link>
        </div>
        <Footer />
      </main>
    );
  }

  const articleSchema = buildArticleSchema(article, content);
  const relatedArticles = getRelatedArticleInfo(content.relatedArticles);

  return (
    <main>
      <JsonLd data={articleSchema} />
      <Navbar />

      <article>
        <header className="pt-24 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-5">
              <i className="ri-article-line"></i>
              {article.category}
            </div>
            <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              {content.heroTitle}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {content.heroDescription}
            </p>
            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-foreground-500">
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-time-line mr-1"></i>Время чтения: {article.readTime}</span>
            </div>
          </div>
        </header>

        <div className="relative w-full aspect-[21/9] max-h-[400px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent"></div>
        </div>

        <div className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <div className="prose-custom space-y-8">
              {content.sections.map((section, i) => {
                if (section.type === "heading") {
                  return (
                    <section key={i}>
                      <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                        {section.content}
                      </h2>
                    </section>
                  );
                }
                if (section.type === "subheading") {
                  return (
                    <h3 key={i} className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                      {section.content}
                    </h3>
                  );
                }
                if (section.type === "paragraph") {
                  return (
                    <p key={i} className="text-foreground-700 text-sm md:text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: section.content || "" }} />
                  );
                }
                if (section.type === "list" && section.items) {
                  return (
                    <section key={i}>
                      {section.content && (
                        <p className="text-foreground-800 text-sm font-medium mb-3">{section.content}</p>
                      )}
                      <ul className="space-y-2.5">
                        {section.items.map((item, j) => (
                          <li key={j} className="flex gap-3">
                            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                              <i className="ri-check-line text-[10px]"></i>
                            </div>
                            <span className="text-foreground-700 text-sm leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                }
                if (section.type === "table" && section.tableData) {
                  return (
                    <section key={i}>
                      {section.content && (
                        <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-3">{section.content}</h3>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b-2 border-background-200">
                              {section.tableData.headers.map((h, j) => (
                                <th key={j} className="py-3 pr-4 font-heading font-semibold text-foreground-950 text-sm whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="text-foreground-700 text-xs md:text-sm">
                            {section.tableData.rows.map((row, j) => (
                              <tr key={j} className="border-b border-background-100 last:border-b-0">
                                {row.map((cell, k) => (
                                  <td key={k} className={`py-3 pr-4 whitespace-nowrap ${k === 0 ? "font-medium text-foreground-900" : ""}`}>
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  );
                }
                if (section.type === "cta") {
                  return (
                    <section key={i} className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200">
                      <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-3">
                        Готовы начать шоппинг?
                      </h2>
                      <p className="text-foreground-700 text-sm md:text-base mb-5 leading-relaxed">
                        Перейдите к таблицам <strong className="text-foreground-950">Yoybuy Sheet</strong> на kaspreadsheet.com и начните находить лучшие товары уже сегодня. Выбирайте категорию, копируйте ссылки и оформляйте заказы через Yoybuy.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                          to="/"
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-heading font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-home-line"></i>
                          Вернуться на главную
                        </Link>
                        <a
                          href="https://kaspreadsheet.com"
                          target="_blank"
                          rel="noopener nofollow"
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-heading font-semibold text-sm hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-external-link-line"></i>
                          Перейти к таблицам
                        </a>
                      </div>
                    </section>
                  );
                }
                return null;
              })}

              {relatedArticles.length > 0 && (
                <section className="pt-6 border-t border-background-200/70">
                  <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5">
                    Похожие статьи
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra!.slug}
                        to={`/${ra!.slug}`}
                        className="group flex flex-col p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                      >
                        <span className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium w-fit mb-2 whitespace-nowrap">
                          {ra!.category}
                        </span>
                        <h3 className="font-heading font-semibold text-foreground-950 text-xs leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra!.title}
                        </h3>
                        <div className="flex items-center gap-1 mt-2 text-primary-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                          <span>Читать</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              <section className="text-center pt-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors text-sm cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Вернуться на главную страницу
                </Link>
              </section>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}